// Recon List (tl-app → partner-apis): the open-recon list and its "Collect
// Cash before recon" part. The same four routes partner-web's recon list uses, under `/leader/db-manage-web`: contract in
// docs/partner-apis/collected-cash.md and recon-dbs.md (citymall-services).
// Every response uses the house envelope `{ status, data }`; callers read
// `response.data.data`.
//
// The OTP never reaches this app: the delivery boy reads it off the Recon
// page of the partner-db app and says it to the CMO, who types it here.

import { api } from "../api";

const BASE = "/leader/db-manage-web";

/**
 * @typedef {Object} OpenReconRow
 * @property {string} date YYYY-MM-DD
 * @property {number} db_id
 * @property {string} db_text "Name - phone"
 * @property {number} cash_expected
 * @property {number} qty_expected
 * @property {"Start"|"Resume"} buttonTxt
 * @property {boolean} collect_cash_enabled always true (payload compat)
 * @property {number} cash_deliverable COD delivered to customers (the cap)
 * @property {number} cash_collected_early verified handovers so far
 * @property {{trip_id: string, amount: number, expires_at: string}|null} pending_cash_request
 */

/**
 * `GET /recon-dbs?date&type=OPEN` → `{ reconsList: OpenReconRow[] }` — the
 * DBs with an open recon for that OFD date (the partner-web Open Recon tab).
 */
export const getOpenRecons = ({ date }) =>
  api.get(`${BASE}/recon-dbs`, { params: { date, type: "OPEN" } });

// The sheet handles the feature's own 400s (cap exceeded, code expired or
// locked, recon closed) and the 412 OTP mismatch inline. The shared
// interceptor would snackbar every 400 and resolve a 412 as a response, so
// both are let through as responses and read with `readHandledResponse`.
const acceptHandled = (status) =>
  (status >= 200 && status < 300) || status === 400 || status === 412;

/**
 * `GET /collected-cash/summary?db_id&ofd_date` → `{ enabled, cash_deliverable,
 * total_collected, remaining, pending_request: {trip_id, amount, expires_at,
 * server_time} | null, txns: [{id, trip_id, amount, verified_at,
 * verified_by_name}] }`. Never includes the OTP.
 */
export const getCollectedCashSummary = ({ db_id, ofd_date }) =>
  api.get(`${BASE}/collected-cash/summary`, {
    params: { db_id, ofd_date },
    validateStatus: acceptHandled,
  });

/**
 * `POST /collected-cash/request` → `{ trip_id, log_id, amount, expires_at,
 * server_time, reused }`. Omit `trip_id` for a new handover; pass the pending
 * one with `resend: true` for "Send new OTP" (fresh code and timer).
 * Resolves with a 400 response for COLLECTED_CASH_EXCEEDS_DELIVERED,
 * RECON_ALREADY_COMPLETED, COLLECTED_CASH_TRIP_ALREADY_VERIFIED, DB_NOT_ACTIVE.
 */
export const requestCollectedCash = ({
  db_id,
  ofd_date,
  amount,
  trip_id,
  resend,
}) =>
  api.post(
    `${BASE}/collected-cash/request`,
    {
      db_id,
      ofd_date,
      amount: Number(amount),
      ...(trip_id ? { trip_id } : {}),
      resend: Boolean(resend),
    },
    { validateStatus: acceptHandled },
  );

/**
 * `POST /collected-cash/verify` → `{ verified, amount, verified_at,
 * total_collected, remaining }`. Resolves with a 412 response for
 * COLLECTED_CASH_OTP_MISMATCH (data.attempts_left) and a 400 for
 * COLLECTED_CASH_OTP_EXPIRED / COLLECTED_CASH_OTP_LOCKED.
 */
export const verifyCollectedCash = ({ db_id, ofd_date, trip_id, otp }) =>
  api.post(
    `${BASE}/collected-cash/verify`,
    { db_id, ofd_date, trip_id, otp: String(otp).trim() },
    { validateStatus: acceptHandled },
  );
